/**
 * Files attached in the chat, carried onto a request filed from it.
 *
 * A request form card used to send only the files uploaded into its own
 * ATTACHMENT questions, so the `.msg` someone attached with "please install
 * this" was named in the transcript note and never reached the request
 * (LIVE #10578, 8 Oct).
 *
 * Import-free and erasable-syntax only, so `tests/chat-files.test.mjs` can load
 * it straight into Node.
 */

/** One file the person attached to a chat turn. */
export interface ChatFile {
  attachmentId: string;
  name: string;
}

/** A file the server says did not reach the request (`UnattachedFile`). */
export interface UnattachedFileRef {
  attachment_id: string;
  filename?: string | null;
}

/**
 * Every file attached in the chat up to and including the turn `messageId`
 * answered, oldest first, each once.
 *
 * Cumulative on purpose: the form often arrives a turn or two after the file
 * ("Which version?" first), and the file is still what the request is about.
 * The card lets the person take any of them off with ×.
 *
 * @param orderedMessageIds The conversation's assistant message ids, in order.
 * @param filesByMessage    Files each turn carried, by the assistant message that answered it.
 * @param messageId         The turn the form belongs to.
 */
export function chatFilesUpTo(
  orderedMessageIds: readonly string[],
  filesByMessage: Readonly<Record<string, readonly ChatFile[]>>,
  messageId: string,
): ChatFile[] {
  if (!orderedMessageIds.includes(messageId)) {
    return [...(filesByMessage[messageId] ?? [])];
  }
  const seen = new Set<string>();
  const files: ChatFile[] = [];
  for (const id of orderedMessageIds) {
    for (const file of filesByMessage[id] ?? []) {
      if (!seen.has(file.attachmentId)) {
        seen.add(file.attachmentId);
        files.push(file);
      }
    }
    if (id === messageId) {
      break;
    }
  }
  return files;
}

/**
 * The names to show for files that did not reach the request.
 *
 * The server names what it still holds; for anything else (expired before it
 * was sent) the browser's own record of the upload is used, then `fallback`.
 */
export function unattachedFileNames(
  unattached: readonly UnattachedFileRef[],
  known: readonly ChatFile[],
  fallback: string,
): string[] {
  return unattached.map(
    (file) =>
      file.filename ??
      known.find((item) => item.attachmentId === file.attachment_id)?.name ??
      fallback,
  );
}
